/**
 * OKR Detail Sidebar Component
 * Displays comprehensive OKR details when a node is clicked
 */

import React from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { X, ExternalLink, Edit, Calendar, User, Building2, Target } from 'lucide-react';
import { OKR } from '../../types/okr';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { keyResultProgressPercent } from '../../utils/progress';

interface OKRDetailSidebarProps {
  okr: OKR | null;
  isOpen: boolean;
  onClose: () => void;
}

export const OKRDetailSidebar: React.FC<OKRDetailSidebarProps> = ({ okr, isOpen, onClose }) => {
  const openWithReturn = useOpenWithReturn();
  const { user } = useAuth();
  const { can } = usePermissions();

  if (!okr) return null;

  // Check user relationships to this OKR
  const isOwner = user?.id === okr.ownerId;
  const isAccountAdmin = user?.role === 'account_admin';
  const isUserManager = user?.role === 'manager'; // User has manager role
  const isOKROwnerManager = user?.id === okr.owner?.managerId; // User is the manager OF this OKR's owner
  const hasOKRUpdatePermission = can('okr', 'update');

  // Types outside the four the product supports are OWNER-ONLY on the backend:
  // `classifyOkrType` in permission.service.ts buckets anything it does not recognise as
  // owner-only, for reads and writes alike. Legacy 'personal' rows (the type was retired
  // 2026-08-08 and deliberately NOT re-typed, because re-typing would widen who can read
  // them) land here. Mirror that classification rather than naming the retired type, so
  // this component never offers a manager a control the API will refuse.
  const isOwnerOnlyType = !['company', 'department', 'team', 'individual'].includes(okr.type);

  // View permission logic:
  // - Account Admin: Can view all OKRs
  // - Manager: Can view all OKRs EXCEPT owner-only ones belonging to others
  //   - Can view if: they own it, OR it's their direct report's non-owner-only OKR
  // - Employee: Can only view their own OKRs
  const canViewFullDetails = (() => {
    // Account Admin can view everything
    if (isAccountAdmin) return true;

    // Owner can always view their own OKRs
    if (isOwner) return true;

    // Manager viewing OKRs
    if (isUserManager) {
      // Manager can view direct report's OKRs, except owner-only ones
      if (isOKROwnerManager && !isOwnerOnlyType) return true;
      // Manager can view company/department/team OKRs for alignment context
      if (['company', 'department', 'team'].includes(okr.type)) return true;
      return false;
    }

    // Employee/Viewer: can only view their own (already checked above)
    return false;
  })();

  // Edit permission logic:
  // - Account Admin: Can edit all OKRs
  // - Manager: Can edit own + direct reports' OKRs (EXCEPT owner-only types)
  // - Employee: Can only edit their own OKRs
  // - OKR must not be in final state
  const canEdit = (() => {
    if (!hasOKRUpdatePermission) return false;
    if (['completed', 'cancelled', 'deleted'].includes(okr.status)) return false;

    // Account Admin can edit everything
    if (isAccountAdmin) return true;

    // Owner can edit their own OKRs
    if (isOwner) return true;

    // Manager can edit direct report's OKRs, except owner-only ones
    if (isUserManager && isOKROwnerManager && !isOwnerOnlyType) return true;

    return false;
  })();

  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'secondary' => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'secondary'> = {
      on_track: 'success',
      at_risk: 'warning',
      off_track: 'danger',
      completed: 'success',
      active: 'success',
      draft: 'secondary',
      // S1-g: 'secondary' matches the previous fallback, so this is documentation rather
      // than a visual change — but it makes the omission impossible to reintroduce silently.
      closed: 'secondary',
      deleted: 'secondary',
    };
    return variants[status] || 'secondary';
  };

  // Get type-specific colors
  const getTypeColors = (type: string) => {
    switch (type) {
      case 'company':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          badgeBg: 'bg-blue-100',
          badgeText: 'text-blue-800',
          progressBg: 'bg-blue-200',
          progressFill: 'bg-blue-600',
        };
      case 'team':
      case 'department':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-900',
          badgeBg: 'bg-green-100',
          badgeText: 'text-green-800',
          progressBg: 'bg-green-200',
          progressFill: 'bg-green-600',
        };
      case 'individual':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-900',
          badgeBg: 'bg-purple-100',
          badgeText: 'text-purple-800',
          progressBg: 'bg-purple-200',
          progressFill: 'bg-purple-600',
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-900',
          badgeBg: 'bg-gray-100',
          badgeText: 'text-gray-800',
          progressBg: 'bg-gray-200',
          progressFill: 'bg-gray-600',
        };
    }
  };

  const colors = getTypeColors(okr.type);
  const keyResults = okr.keyResults || [];

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          overflow-y-auto
        `}
      >
        {/* Header */}
        <div className={`sticky top-0 ${colors.bg} border-b-2 ${colors.border} px-6 py-4 z-10`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold ${colors.text} uppercase tracking-wide px-2 py-1 ${colors.badgeBg} rounded`}>
                  {okr.type} OKR
                </span>
                <Badge variant={getStatusVariant(okr.status)} size="sm">
                  {okr.status.replace('_', ' ')}
                </Badge>
              </div>
              <h2 className={`text-lg font-bold ${colors.text} leading-tight`}>
                {okr.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors ml-2"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className={`font-medium ${colors.text}`}>Overall Progress</span>
              <span className={`font-bold ${colors.text}`}>{okr.progress || 0}%</span>
            </div>
            <div className={`w-full ${colors.progressBg} rounded-full h-3 overflow-hidden`}>
              <div
                className={`h-full ${colors.progressFill} rounded-full transition-all duration-500`}
                style={{ width: `${okr.progress || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Description - always visible */}
          {okr.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Description
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">{okr.description}</p>
            </div>
          )}

          {/* Restricted access notice for individual OKRs user doesn't own */}
          {!canViewFullDetails && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                This is a private Individual OKR. You can see it in the alignment map,
                but detailed information is only visible to the owner and their manager.
              </p>
            </div>
          )}

          {/* Owner Information - only show full details if permitted */}
          {canViewFullDetails ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Owner
              </h3>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {okr.owner.firstName} {okr.owner.lastName}
                </p>
                {(okr.owner.deletedAt || okr.owner.status === 'inactive') && (
                  <span
                    className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    title={`${okr.owner.firstName} ${okr.owner.lastName} is ${okr.owner.deletedAt ? 'a deleted' : 'an inactive'} user`}
                  >
                    {okr.owner.deletedAt ? 'Deleted user' : 'Inactive user'}: {okr.owner.firstName} {okr.owner.lastName}
                  </span>
                )}
                {/* Absent unless the caller is entitled to this owner's address.
                    Render nothing rather than a hollow line; department below
                    remains as the non-PII identifier. */}
                {okr.owner.email && (
                  <p className="text-xs text-gray-600">{okr.owner.email}</p>
                )}
                {okr.owner.department && (
                  <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                    <Building2 className="w-3 h-3" />
                    {okr.owner.department}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Owner
              </h3>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {okr.owner.firstName} {okr.owner.lastName}
                </p>
                {(okr.owner.deletedAt || okr.owner.status === 'inactive') && (
                  <span
                    className="mt-1 inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    title={`${okr.owner.firstName} ${okr.owner.lastName} is ${okr.owner.deletedAt ? 'a deleted' : 'an inactive'} user`}
                  >
                    {okr.owner.deletedAt ? 'Deleted user' : 'Inactive user'}: {okr.owner.firstName} {okr.owner.lastName}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Time Period - only show if permitted */}
          {canViewFullDetails && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Time Period
              </h3>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Start:</span>
                  <span className="font-medium text-gray-900">{formatDate(okr.startDate)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">End:</span>
                  <span className="font-medium text-gray-900">{formatDate(okr.endDate)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Key Results - only show if permitted */}
          {canViewFullDetails && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Key Results ({keyResults.length})
              </h3>
              {keyResults.length > 0 ? (
                <div className="space-y-4">
                  {keyResults.map((kr, index) => {
                    const krProgress = keyResultProgressPercent(kr);
                    return (
                      <div key={kr.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              KR {index + 1}
                            </span>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {kr.title}
                            </p>
                          </div>
                          <span className="text-sm font-bold text-gray-700 ml-2">
                            {krProgress}%
                          </span>
                        </div>

                        <div className={`w-full ${colors.progressBg} rounded-full h-2 overflow-hidden mb-2`}>
                          <div
                            className={`h-full ${colors.progressFill} rounded-full transition-all duration-300`}
                            style={{ width: `${krProgress}%` }}
                          />
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Current: {kr.currentValue} {kr.unit}</span>
                          <span>Target: {kr.targetValue} {kr.unit}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic bg-gray-50 rounded-lg p-3">
                  No key results defined
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-4 border-t border-gray-200">
            {canViewFullDetails ? (
              <>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => openWithReturn(`/okrs/${okr.id}`)}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Full Details
                </Button>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => openWithReturn(`/okrs/${okr.id}/edit`)}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit OKR
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 text-center py-2">
                You don't have permission to view full details of this OKR.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
